import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Eye, ShoppingBag, Plus, PencilLine, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { Pagination } from "@/components/common/Pagination";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { DialogShell, Field } from "@/components/common/FormShell";
import { UserName } from "@/components/common/EntityName";
import { productsApi } from "@/lib/api";
import type { Product } from "@/lib/api/types";
import { fmtCurrency, fmtRelativeTime } from "@/lib/utils";
import { usePagedQuery, PAGE_SIZE } from "@/lib/usePagedQuery";
import { ProductDetailDialog } from "./ProductDetailDialog";
import { useAuthStore } from "@/lib/auth/store";

export default function ProductsList() {
  const qc = useQueryClient();
  const [detail, setDetail] = useState<Product | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Product | null>(null);
  const [deleting, setDeleting] = useState<Product | null>(null);

  const {
    q, setQ, page, setPage,
    items, totalItems, totalPages, isFetching,
  } = usePagedQuery<Product>({
    key: ["products"],
    fetcher: productsApi.list,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["products"] });
    setEditorOpen(false);
    setEditing(null);
    setDeleting(null);
  };
  const remove = useMutation({ mutationFn: (id: string) => productsApi.remove(id), onSuccess: refresh });

  return (
    <>
      <PageHeader
        title="Products"
        subtitle={`${totalItems} marketplace items. Create, edit stock and prices, or remove listings.`}
        action={
          <Button onClick={() => { setEditing(null); setEditorOpen(true); }}>
            <Plus className="size-4" /> Add product
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          <div className="border-b border-border p-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products…" />
            </div>
          </div>

          {items.length === 0 ? (
            <EmptyState
              icon={ShoppingBag}
              title={isFetching ? "Loading…" : "No products"}
              description="Nothing in the marketplace matches this search."
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Stock</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Listed</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" onClick={() => setDetail(p)}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {p.imageUrl ? (
                          <img src={p.imageUrl} alt="" className="size-9 rounded-lg object-cover" />
                        ) : (
                          <div className="flex size-9 items-center justify-center rounded-lg bg-muted">
                            <ShoppingBag className="size-4 text-muted-foreground" />
                          </div>
                        )}
                        <p className="max-w-48 truncate text-sm font-semibold">{p.name}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm"><UserName id={p.sellerId} /></TableCell>
                    <TableCell className="text-sm font-semibold">{fmtCurrency(p.price, "TZS")}</TableCell>
                    <TableCell className="text-sm">
                      {p.stock <= 0 ? <Badge variant="destructive">Out of stock</Badge> : p.stock}
                    </TableCell>
                    <TableCell>
                      {p.isActive ? <Badge variant="success">Active</Badge> : <Badge variant="muted">Inactive</Badge>}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtRelativeTime(p.createdAt)}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => { setEditing(p); setEditorOpen(true); }}>
                          <PencilLine className="size-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" className="border-destructive/40 text-destructive" onClick={() => setDeleting(p)}>
                          <Trash2 className="size-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDetail(p)}>
                          <Eye className="size-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Pagination page={page} size={PAGE_SIZE} totalItems={totalItems} totalPages={totalPages} onChange={setPage} />
        </CardContent>
      </Card>

      <ProductDetailDialog product={detail} onClose={() => setDetail(null)} />
      <ProductEditorDialog open={editorOpen} product={editing} onOpenChange={setEditorOpen} onSaved={refresh} />

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!.id)}
        icon={Trash2}
        tone="destructive"
        title="Delete this product?"
        description={`"${deleting?.name}" is removed from the marketplace permanently.`}
        confirmLabel="Delete product"
        loading={remove.isPending}
      />
    </>
  );
}

function ProductEditorDialog({
  open, product, onOpenChange, onSaved,
}: {
  open: boolean;
  product: Product | null;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
}) {
  const session = useAuthStore((s) => s.session);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [stock, setStock] = useState("0");
  const [imageUrl, setImageUrl] = useState("");
  const [isActive, setIsActive] = useState(true);

  useEffect(() => {
    if (open) {
      setName(product?.name ?? "");
      setDescription(product?.description ?? "");
      setPrice(product ? String(product.price) : "");
      setStock(product ? String(product.stock) : "0");
      setImageUrl(product?.imageUrl ?? "");
      setIsActive(product ? product.isActive : true);
    }
  }, [open, product]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        description: description.trim() || undefined,
        price: Number(price),
        stock: Number(stock) || 0,
        imageUrl: imageUrl.trim() || undefined,
        isActive,
      };
      return product
        ? productsApi.update(product.id, body)
        : productsApi.create({ ...body, sellerId: session?.userId });
    },
    onSuccess: onSaved,
  });

  const valid = name.trim().length > 0 && Number(price) > 0;

  return (
    <DialogShell
      open={open}
      onOpenChange={onOpenChange}
      icon={product ? <PencilLine className="size-7" /> : <Plus className="size-7" />}
      title={product ? "Edit product" : "Add product"}
      description={product ? "Changes are visible to buyers immediately." : "Lists a new item in the marketplace."}
    >
      <div className="mt-5 space-y-3 text-left">
        <Field label="Name">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Digital thermometer" />
        </Field>
        <Field label="Description">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-border bg-background p-3 text-sm outline-none focus:ring-2 focus:ring-primary/40"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Price (TZS)">
            <Input type="number" min="0" value={price} onChange={(e) => setPrice(e.target.value)} />
          </Field>
          <Field label="Stock">
            <Input type="number" min="0" value={stock} onChange={(e) => setStock(e.target.value)} />
          </Field>
        </div>
        <Field label="Image URL (optional)">
          <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://…" />
        </Field>
        <label className="flex cursor-pointer items-center gap-2 text-sm font-semibold">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="size-4 accent-[var(--color-primary)]" />
          Active (visible to buyers)
        </label>
        <div className="flex gap-2 pt-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Saving…" : product ? "Save changes" : "Create product"}
          </Button>
        </div>
      </div>
    </DialogShell>
  );
}
